/* eslint-disable import/no-cycle */
import { Box } from '@hivebrite/design-system/Box';

import { sectionImageRecommendedSize } from 'hb-builder/components/BuilderComponents/constants/imageSizes';
import ColorSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/ColorSetting';
import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import { useBuilderContext, useBuilderConfigContext } from 'hb-builder/hooks';
import type { ComponentSettingsTab } from 'hb-builder/types/settings';
import type { HeroItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/heroItemAttributes';

import ImageBackground from './ImageBackground';
import VideoBackground from './VideoBackground';

const BackgroundImage = ({
  item,
  onChange,
  permissions: {
    can: {
      canHeroBeFullWidth,
      canHaveRoundedCorner,
      imageBg: canSetImageBackground,
      videoBg: canSetVideoBackground,
    },
  } = { can: {} },
}: Omit<ComponentSettingsTab<HeroItemAttributes>, 'defaultAttributes'>) => {
  const { updateAttributes } = useBuilderContext();
  const {
    features: { color },
    hivebrite: { networkColors, networkTheming },
  } = useBuilderConfigContext();
  const colorSettingsProps = {
    color,
    networkColors,
    networkTheming,
  };
  const settingProps = { item, onChange };
  const { attributes } = item;

  const isImageBackground =
    attributes?.backgroundType === 'image' || !attributes?.backgroundType;

  const isVideoBackground = !isImageBackground;

  const handleBackgroundTypeChange = ({ value }) => {
    const values = [
      {
        attribute: 'backgroundType',
        value,
      },
    ];

    values.push(...handleCustomAttributes(value === 'video'));

    updateAttributes({
      id: item.id,
      values,
    });
  };

  const handleCustomAttributes = (isVideo) => {
    const videoDefaultAttributes = {
      containerPadding: '0px 0px 0px 0px',
      darkenedVideo: true,
      videoBackgroundArrangement: 'fill',
      videoBackgroundPosition: 'center',
    };
    const attributes: {
      attribute: string;
      value: any;
    }[] = [];

    Object.keys(videoDefaultAttributes).forEach((attribute) =>
      attributes.push({
        attribute,
        value: isVideo ? videoDefaultAttributes[attribute] : undefined,
      }),
    );

    return attributes;
  };

  return (
    <>
      {canSetImageBackground && (
        <>
          {isImageBackground && (
            <InputTitle
              boxProps={{ mb: 3 }}
              id={`containerBackgroundColor-bg-${item.id}`}
              label={__('Background')}
            />
          )}
          <ColorSetting
            settingKey="containerBackgroundColor"
            title={__('Color background')}
            transparent
            {...settingProps}
            {...colorSettingsProps}
          />
          {isImageBackground && (
            <>
              <InputTitle
                boxProps={{ mb: 3 }}
                id={`containerBackgroundColor-bg-image-${item.id}`}
                label={__('Image background')}
              />
              <Box my={2}>
                {s__(
                  'Builder|Recommended %{size}px. The file must not exceed 2.5 MB',
                ).i__({
                  size: sectionImageRecommendedSize,
                })}
              </Box>
            </>
          )}
        </>
      )}
      <VideoBackground
        canHeroBeFullWidth={canHeroBeFullWidth}
        canSetVideoBackground={canSetVideoBackground}
        handleBackgroundTypeChange={handleBackgroundTypeChange}
        isVideoBackground={isVideoBackground}
        item={item}
        onChange={onChange}
      />

      <ImageBackground
        canHaveRoundedCorner={canHaveRoundedCorner}
        canHeroBeFullWidth={canHeroBeFullWidth}
        isImageBackground={isImageBackground}
        item={item}
        onChange={onChange}
      />
    </>
  );
};

export default BackgroundImage;
